import SaveIcon from '@mui/icons-material/Save';
import { Button } from '@mui/material';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { EnhancedAccordion } from 'components/enhanced/accordion/EnhancedAccordion';
import { EnhancedCard } from 'components/enhanced/card/EnhancedCard';
import { supplierTransportDocumentActiveLineService } from 'features/inventory/transport-documents/SupplierTrasportActiveDocument/api/supplier-transport-active-document-line.service';
import { t } from 'i18next';
import { useState } from 'react';
import toast from 'shared/utils/toast';
import { v4 as uuidv4 } from 'uuid';
import { transportDocumentActiveLineService } from '../../api/transport-active-document-line.service';
import TransportActiveDocumentLineForm from '../form/TransportActiveDocumentLineForm';
import { TransportOrderLeftovers } from './TransportOrderLeftovers';
import UpdateTransportActiveDocumentLineDialog from './UpdateTransportActiveDocumentLineDialog';
import { EnhancedDialog } from 'components/enhanced';
import { TransportActiveDocumentLinesColumns } from '../../transport-active-document-lines';

interface ActiveDDTProductsProps {
  isSupplierDdt: boolean;
  translationPrefix: string;
  store: any;
}

// Lista Merci
export const ActiveDDTProducts = ({
  isSupplierDdt,
  translationPrefix,
  store
}: ActiveDDTProductsProps) => {
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editRow, setEditRow] = useState<any>(null);

  // Gestione Dialog di Modifica della singola riga
  const handleOpenEdit = (row: any) => {
    setEditRow(row);
    setOpenEditDialog(true);
  };

  const onSubmitEditLine = async (id: any, params: any) => {
    const activeDocumentLineService = isSupplierDdt
      ? supplierTransportDocumentActiveLineService
      : transportDocumentActiveLineService;

    try {
      await activeDocumentLineService.update(id, params);
      toast.success(t('global.updated-successfully', 'Aggiornato con successo'));

      const updatedLines = store.ddt.lines.map((line: any) =>
        line.id === id ? { ...line, ...params } : line
      );
      store.setDdt({ ...store.ddt, lines: updatedLines });
      setOpenEditDialog(false);
      store.getDDTById();
    } catch (err) {
      toast.error(t('global.update-error'));
    }
  };

  // Inserimento riga locale nel DDT prima del salvataggio definitivo
  const onSubmitNewRow = (values: any) => {
    if (values.quantity > 0) {
      const product_batch_code = values['product_batch']?.product_batch ?? 'NO_BATCH';

      values['product_code'] = values['product']?.code;
      values['product_description'] = values['product']?.description?.replace(
        `${values['product']?.code} - `,
        ''
      );
      values['expiration_date'] = values['product_batch']?.expiration_date ?? '';
      values['location'] = values['product_batch']?.location ?? '';
      values['product_batch'] = product_batch_code;
      values['document'] = store.ddt.id;
      values['notes'] = values['product_batch']?.location ?? '';
      values['id'] = uuidv4();

      store.setDdt({ ...store.ddt, lines: [...(store.ddt.lines || []), values] });
    } else {
      toast.warning(t(`${translationPrefix}.invalid-quantity`, 'Quantità non valida'));
    }
  };

  const columns: GridColDef[] = TransportActiveDocumentLinesColumns(
    true,
    store.deleteLine,
    handleOpenEdit,
    store.handleOpenInfoDialog,
    store.isDisabledEdit,
    store.isClosed,
    isSupplierDdt
  );

  const changes: boolean = store.changesToSave();

  return (
    <>
      {store.ddt && !store.isDisabledEdit && !store.isClosed && (
        <EnhancedAccordion
          title={t(`${translationPrefix}.add-element`)}
          subtitle="Aggiungi una nuova riga compilando i dettagli del prodotto e la quantità"
          buttons={
            <Button
              variant="contained"
              sx={{
                display: changes ? 'inline-flex' : 'none',
                backgroundColor: '#1d70b8',
                '&:hover': { backgroundColor: '#15528a' }
              }}
              onClick={store.saveBulkLines}
              startIcon={<SaveIcon />}>
              Salva modifiche
            </Button>
          }>
          <TransportActiveDocumentLineForm
            ddt={store.ddt}
            onSubmit={onSubmitNewRow}
            products={store.productOptions}
            setProduct={store.setProductToAdd}
          />
        </EnhancedAccordion>
      )}

      <EnhancedCard
        title={t(`${translationPrefix}.product-list-title`)}
        subtitle={t(`${translationPrefix}.product-list-subtitle`)}>
        <DataGridPremium
          density="compact"
          rows={store.ddt?.lines ?? []}
          sx={{ border: 'none' }}
          disableRowSelectionOnClick
          pagination
          columns={columns}
          pageSizeOptions={[5, 10, 20, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10, page: 0 }
            },
            pinnedColumns: {
              right: ['actions']
            }
          }}
          slots={{
            noRowsOverlay: () => (
              <div className="flex items-center justify-center h-full text-slate-400 text-sm">
                Nessun prodotto presente
              </div>
            )
          }}
        />
      </EnhancedCard>

      <TransportOrderLeftovers ddt={store.ddt} prefix={translationPrefix} />

      {/* Dialog component to edit contract line details */}
      <EnhancedDialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        title={t(`pages.contract-line.edit.title`)}
        submitLabel={t('actions.save', 'Salva')}
        cancelLabel={t('actions.reset', 'Annulla')}
        maxWidth="md"
        submitButtonProps={{
          type: 'submit',
          form: 'updateTransportActiveDocumentLine'
        }}>
        <UpdateTransportActiveDocumentLineDialog
          trasportActiveDocumentLine={editRow}
          ddt={store.ddt}
          onSubmit={onSubmitEditLine}
        />
      </EnhancedDialog>
    </>
  );
};
